import { useEffect, useRef, useState } from 'react'
import { Loader2, Maximize2, Minimize2, Vault, Spline, Group } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/app/store/auth'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { MARKER_COLORS } from './mapConstants'

declare global {
    interface Window {
        google: any
        initGoogleMapsInline: () => void
    }
}

export interface GoogleMapLocation {
    id: number | string
    name: string
    type: 'dc' | 'vault'
    latitude: number
    longitude: number
    address?: string
    category?: string
    vaultId?: string
    objectCount?: number
    versionCount?: number
}

export interface GoogleMapConnection {
    fromLocationId: number | string
    toLocationId: number | string
    objectCount?: number
    versionCount?: number
    totalSize?: number
    bandwidth?: number
    latencyPercentage?: number
    tcpPercentage?: number
    encryptionPercentage?: number
}

interface GoogleMapInlineProps {
    locations: GoogleMapLocation[]
    connections?: GoogleMapConnection[]
    pathLocations?: GoogleMapLocation[]
    pathConnections?: GoogleMapConnection[]
    compact?: boolean
    height?: string
}

function loadGoogleMapsScript(apiKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const scriptId = 'google-maps-script'
        if (document.getElementById(scriptId)) {
            if (window.google?.maps) { resolve(); return }
        }
        const callbackName = 'initGoogleMapsInline'
        window[callbackName] = () => resolve()
        const script = document.createElement('script')
        script.id = scriptId
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&loading=async&callback=${callbackName}`
        script.onerror = () => reject(new Error('Failed to load Google Maps'))
        document.head.appendChild(script)
    })
}

const TT = {
    wrap: 'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:8px 10px;min-width:160px;max-width:240px;line-height:1.45;',
    title: 'font-size:13px;font-weight:600;color:#111;letter-spacing:-0.01em;',
    badge: (bg: string) => `display:inline-block;font-size:9px;font-weight:500;color:#fff;background:${bg};border-radius:3px;padding:1px 6px;letter-spacing:0.02em;text-transform:uppercase;`,
    label: 'font-size:10px;color:#888;font-weight:400;',
    value: 'font-size:10px;color:#222;font-weight:500;',
    mono: 'font-size:10px;color:#222;font-weight:500;font-family:"SF Mono",Monaco,Consolas,monospace;letter-spacing:0.02em;',
    divider: 'border-top:1px solid #e8e8e8;margin:5px 0;',
    section: 'font-size:10px;font-weight:600;color:#555;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:3px;',
    row: 'display:flex;justify-content:space-between;gap:12px;padding:1px 0;',
    dot: (bg: string) => `display:inline-block;width:6px;height:6px;border-radius:50%;background:${bg};flex-shrink:0;`,
}

function createTooltip(loc: GoogleMapLocation, connectedNames?: { name: string; type: 'dc' | 'vault' }[]): string {
    const color = loc.type === 'dc' ? MARKER_COLORS.dc : MARKER_COLORS.vault
    const typeLabel = loc.type === 'dc' ? 'Data centre' : 'Vault'
    let html = `<div style="${TT.wrap}">`
    html += `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">`
    html += `<span style="${TT.dot(color)}"></span>`
    html += `<span style="${TT.title}">${loc.name}</span>`
    html += `</div>`
    html += `<span style="${TT.badge(color)}">${typeLabel}</span>`
    if (loc.address) {
        html += `<div style="margin-top:4px;font-size:10px;color:#666;">${loc.address}</div>`
    }
    if (loc.type === 'vault' && (loc.vaultId || loc.objectCount !== undefined)) {
        html += `<div style="${TT.divider}"></div>`
        if (loc.vaultId) html += `<div style="${TT.row}"><span style="${TT.label}">Vault ID</span><span style="${TT.mono}">${loc.vaultId}</span></div>`
        if (loc.objectCount !== undefined) html += `<div style="${TT.row}"><span style="${TT.label}">Objects</span><span style="${TT.value}">${loc.objectCount.toLocaleString()}</span></div>`
        if (loc.versionCount !== undefined) html += `<div style="${TT.row}"><span style="${TT.label}">Versions</span><span style="${TT.value}">${loc.versionCount.toLocaleString()}</span></div>`
    }
    if (connectedNames && connectedNames.length > 0) {
        const heading = loc.type === 'dc' ? 'Vaulted to' : 'Receives from'
        html += `<div style="${TT.divider}"></div>`
        html += `<div style="${TT.section}">${heading}</div>`
        connectedNames.forEach(c => {
            const cColor = c.type === 'dc' ? MARKER_COLORS.dc : MARKER_COLORS.vault
            html += `<div style="display:flex;align-items:center;gap:5px;padding:1px 0;font-size:10px;color:#333;"><span style="${TT.dot(cColor)}"></span>${c.name}</div>`
        })
    }
    html += `</div>`
    return html
}

export function GoogleMapInline({ locations, connections = [], pathLocations, pathConnections, compact = false, height = '450px' }: GoogleMapInlineProps) {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const mapRef = useRef<HTMLDivElement>(null)
    const mapInstanceRef = useRef<any>(null)
    const markersRef = useRef<any[]>([])
    const polylinesRef = useRef<any[]>([])
    const clustererRef = useRef<MarkerClusterer | null>(null)
    const [expanded, setExpanded] = useState(false)
    const [mapMode, setMapMode] = useState<'vaults' | 'paths'>('vaults')
    const [clustering, setClustering] = useState(false)
    const [scriptLoaded, setScriptLoaded] = useState(!!window.google?.maps)

    const MAP_ID = 'VAULTSENTINEL_MAP'

    // Fingerprint data to avoid rebuilding map on every render
    const dataFingerprint = useRef('')
    const configFingerprint = useRef('')

    const { data: apiKey = '' } = useQuery({
        queryKey: ['google-maps-api-key'],
        queryFn: async () => {
            const api = (await import('@/lib/api/axios')).default
            const res = await api.get('/api/locations/map/api-key')
            return (res.data as any)?.data?.apiKey || (res.data as any)?.apiKey || ''
        },
        enabled: isAuthenticated,
    })

    // Load script
    useEffect(() => {
        if (!apiKey || scriptLoaded) return
        loadGoogleMapsScript(apiKey).then(() => setScriptLoaded(true)).catch(() => {})
    }, [apiKey, scriptLoaded])

    const isExpanded = expanded

    // Initialize/update map
    useEffect(() => {
        if (!scriptLoaded || !mapRef.current || !window.google?.maps) return

        const activeLocations = mapMode === 'paths' && pathLocations ? pathLocations : locations
        const activeConnections = mapMode === 'paths' && pathConnections ? pathConnections : connections

        // Check if anything actually changed
        const newDataFp = JSON.stringify(activeLocations.map(l => l.id)) + '|' + activeConnections.length
        const newConfigFp = `${mapMode}|${isExpanded}|${clustering}`
        if (newDataFp === dataFingerprint.current && newConfigFp === configFingerprint.current && mapInstanceRef.current) {
            return
        }
        dataFingerprint.current = newDataFp
        configFingerprint.current = newConfigFp

        let centerLat = 0, centerLng = 0, count = 0
        activeLocations.forEach(l => { if (l.latitude && l.longitude) { centerLat += l.latitude; centerLng += l.longitude; count++ } })
        if (count > 0) { centerLat /= count; centerLng /= count }

        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: centerLat || 20, lng: centerLng || 0 },
            zoom: count > 0 ? 4 : 2,
            mapTypeControl: !(compact && !isExpanded),
            streetViewControl: false,
            fullscreenControl: false,
            mapId: MAP_ID,
        })

        // Clear old markers, lines, clusterer
        if (clustererRef.current) {
            clustererRef.current.clearMarkers()
            clustererRef.current = null
        }
        markersRef.current.forEach(m => { if (m.map) m.map = null; else if (m.setMap) m.setMap(null) })
        markersRef.current = []
        polylinesRef.current.forEach(p => p.setMap(null))
        polylinesRef.current = []

        const infoWindow = new window.google.maps.InfoWindow()
        const locationById = new Map<number | string, GoogleMapLocation>()
        const markerById = new Map<number | string, { marker: any; element: HTMLElement }>()

        // Build adjacency for hover
        const adj = new Map<number | string, Set<number | string>>()
        activeConnections.forEach(c => {
            if (!adj.has(c.fromLocationId)) adj.set(c.fromLocationId, new Set())
            if (!adj.has(c.toLocationId)) adj.set(c.toLocationId, new Set())
            adj.get(c.fromLocationId)!.add(c.toLocationId)
            adj.get(c.toLocationId)!.add(c.fromLocationId)
        })

        const polylineMap = new Map<string, any>()

        const highlightConnections = (hoveredId: number | string) => {
            const connected = adj.get(hoveredId) || new Set()
            markerById.forEach(({ element }, id) => {
                const isRelated = id === hoveredId || connected.has(id)
                element.style.opacity = isRelated ? '1' : '0.5'
                element.style.transition = 'opacity 200ms ease'
            })
            polylineMap.forEach((p, key) => {
                const [fromId, toId] = key.split('|||')
                const isRelated = fromId === String(hoveredId) || toId === String(hoveredId)
                p.setOptions({
                    strokeOpacity: isRelated ? 0.9 : 0.2,
                    strokeWeight: isRelated ? 3 : 1.5,
                })
            })
        }

        const resetHighlight = () => {
            markerById.forEach(({ element }) => { element.style.opacity = '1' })
            polylineMap.forEach(p => p.setOptions({ strokeOpacity: 0.4, strokeWeight: 2 }))
        }

        // Check if AdvancedMarkerElement is available
        const hasAdvanced = !!(window.google.maps.marker?.AdvancedMarkerElement)

        // Add markers
        activeLocations.forEach(loc => {
            if (!loc.latitude || !loc.longitude) return
            locationById.set(loc.id, loc)
            const color = loc.type === 'dc' ? MARKER_COLORS.dc : MARKER_COLORS.vault
            const position = { lat: loc.latitude, lng: loc.longitude }

            let marker: any
            let markerElement: HTMLElement

            if (hasAdvanced) {
                // AdvancedMarkerElement with custom pin
                const pin = new window.google.maps.marker.PinElement({
                    background: color,
                    borderColor: '#FFFFFF',
                    glyphColor: '#FFFFFF',
                    scale: 0.9,
                })
                marker = new window.google.maps.marker.AdvancedMarkerElement({
                    map: mapInstanceRef.current,
                    position,
                    title: loc.name,
                    content: pin.element,
                })
                markerElement = marker.element
            } else {
                // Fallback to classic Marker
                marker = new window.google.maps.Marker({
                    position,
                    map: mapInstanceRef.current,
                    title: loc.name,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: color,
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 2,
                        scale: 8,
                    },
                })
                markerElement = document.createElement('div')
            }

            markerById.set(loc.id, { marker, element: hasAdvanced ? markerElement : markerElement })

            const onHover = () => {
                const names = (adj.get(loc.id) || new Set())
                const resolved = Array.from(names).map(id => locationById.get(id)).filter(Boolean).map(l => ({ name: l!.name, type: l!.type }))
                infoWindow.setContent(createTooltip(loc, resolved.length > 0 ? resolved : undefined))
                if (hasAdvanced) {
                    infoWindow.open({ anchor: marker, map: mapInstanceRef.current })
                } else {
                    infoWindow.open(mapInstanceRef.current, marker)
                }
                if (activeConnections.length > 0) highlightConnections(loc.id)
            }

            const onLeave = () => {
                infoWindow.close()
                if (activeConnections.length > 0) resetHighlight()
            }

            if (hasAdvanced) {
                markerElement.addEventListener('mouseenter', onHover)
                markerElement.addEventListener('mouseleave', onLeave)
            } else {
                marker.addListener('mouseover', onHover)
                marker.addListener('mouseout', onLeave)
            }

            markersRef.current.push(marker)
        })

        // Draw connection polylines with hover tooltips
        const pathInfoWindow = new window.google.maps.InfoWindow()
        activeConnections.forEach(c => {
            const from = locationById.get(c.fromLocationId)
            const to = locationById.get(c.toLocationId)
            if (!from || !to) return
            const polyline = new window.google.maps.Polyline({
                path: [
                    { lat: from.latitude, lng: from.longitude },
                    { lat: to.latitude, lng: to.longitude },
                ],
                geodesic: true,
                strokeColor: MARKER_COLORS.arc,
                strokeOpacity: 0.4,
                strokeWeight: 2,
                map: mapInstanceRef.current,
            })

            polyline.addListener('mouseover', (e: any) => {
                const dcColor = MARKER_COLORS.dc
                const vaultColor = MARKER_COLORS.vault
                let html = `<div style="${TT.wrap}">`
                html += `<div style="margin-bottom:6px;">`
                html += `<div style="display:flex;align-items:center;gap:5px;padding:1px 0;"><span style="${TT.dot(dcColor)}"></span><span style="${TT.label}">Location</span></div>`
                html += `<div style="${TT.title};margin-left:11px;margin-bottom:4px;">${from.name}</div>`
                html += `<div style="display:flex;align-items:center;gap:5px;padding:1px 0;"><span style="${TT.dot(vaultColor)}"></span><span style="${TT.label}">Vault</span></div>`
                html += `<div style="${TT.title};margin-left:11px;">${to.name}</div>`
                html += `</div>`
                if (c.bandwidth || c.latencyPercentage || c.tcpPercentage || c.encryptionPercentage) {
                    html += `<div style="${TT.divider}"></div>`
                    html += `<div style="${TT.section}">Network</div>`
                    if (c.bandwidth) html += `<div style="${TT.row}"><span style="${TT.label}">Bandwidth</span><span style="${TT.value}">${c.bandwidth} Mbps</span></div>`
                    if (c.latencyPercentage) html += `<div style="${TT.row}"><span style="${TT.label}">Latency</span><span style="${TT.value}">${c.latencyPercentage}%</span></div>`
                    if (c.tcpPercentage) html += `<div style="${TT.row}"><span style="${TT.label}">TCP Overhead</span><span style="${TT.value}">${c.tcpPercentage}%</span></div>`
                    if (c.encryptionPercentage) html += `<div style="${TT.row}"><span style="${TT.label}">Encryption</span><span style="${TT.value}">${c.encryptionPercentage}%</span></div>`
                }
                if (c.objectCount || c.versionCount) {
                    html += `<div style="${TT.divider}"></div>`
                    html += `<div style="${TT.section}">Data</div>`
                    if (c.objectCount) html += `<div style="${TT.row}"><span style="${TT.label}">Objects</span><span style="${TT.value}">${c.objectCount.toLocaleString()}</span></div>`
                    if (c.versionCount) html += `<div style="${TT.row}"><span style="${TT.label}">Versions</span><span style="${TT.value}">${c.versionCount.toLocaleString()}</span></div>`
                }
                html += `</div>`
                pathInfoWindow.setContent(html)
                pathInfoWindow.setPosition(e.latLng)
                pathInfoWindow.open(mapInstanceRef.current)
            })
            polyline.addListener('mouseout', () => {
                pathInfoWindow.close()
            })

            polylineMap.set(`${c.fromLocationId}|||${c.toLocationId}`, polyline)
            polylinesRef.current.push(polyline)
        })

        // Add marker clustering if enabled
        if (clustering && markersRef.current.length > 0 && hasAdvanced) {
            clustererRef.current = new MarkerClusterer({
                map: mapInstanceRef.current,
                markers: markersRef.current,
            })
        }

        // Fit bounds
        if (markersRef.current.length > 0) {
            const bounds = new window.google.maps.LatLngBounds()
            markersRef.current.forEach(m => {
                const pos = m.getPosition ? m.getPosition() : m.position
                if (pos) bounds.extend(pos)
            })
            mapInstanceRef.current.fitBounds(bounds)
        }
    }, [scriptLoaded, locations, connections, pathLocations, pathConnections, mapMode, isExpanded, clustering])

    const showModeToggle = !!pathLocations
    const hasData = locations.length > 0

    if (!apiKey) return null
    if (!hasData && !compact) return null

    const effectiveCompact = compact && !isExpanded
    const mapHeight = effectiveCompact ? '180px' : isExpanded ? '70vh' : height

    return (
        <div style={effectiveCompact ? { width: 240 } : undefined}>
            {/* Toolbar — shown when not in compact mode */}
            {!effectiveCompact && (
                <div className="flex items-center justify-end mb-2">
                    <div className="flex items-center gap-2">
                        {showModeToggle && (
                            <div className="flex rounded-lg border p-0.5 bg-muted/30 gap-1">
                                <button
                                    type="button"
                                    onClick={() => setMapMode('vaults')}
                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                        mapMode === 'vaults' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    <Vault className="h-3.5 w-3.5 text-blue-600" />
                                    Vaults
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMapMode('paths')}
                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                        mapMode === 'paths' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    <Spline className="h-3.5 w-3.5 text-blue-600" />
                                    Paths
                                </button>
                            </div>
                        )}
                        <Button
                            variant={clustering ? 'default' : 'outline'}
                            size="sm"
                            className="h-7 px-2 text-xs gap-1"
                            onClick={() => setClustering(c => !c)}
                        >
                            <Group className="h-3.5 w-3.5" />
                            Group
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setExpanded(e => !e)}>
                            {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                        </Button>
                    </div>
                </div>
            )}

            {/* Map with expand button outside */}
            <div className="flex items-start gap-1">
                <div className="relative rounded-lg border overflow-hidden flex-1" style={{ height: mapHeight }}>
                    {!scriptLoaded ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted/30">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <div ref={mapRef} className="w-full h-full" />
                    )}
                </div>
                {effectiveCompact && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0 mt-0.5"
                        onClick={() => setExpanded(true)}
                    >
                        <Maximize2 className="h-3.5 w-3.5" />
                    </Button>
                )}
            </div>

            {/* Legend */}
            {!effectiveCompact && (
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                    {(mapMode === 'paths' || locations.some(l => l.type === 'dc')) && (
                        <span className="flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: MARKER_COLORS.dc }} />
                            Data centre
                        </span>
                    )}
                    <span className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: MARKER_COLORS.vault }} />
                        Vault
                    </span>
                    {(mapMode === 'paths' && pathConnections && pathConnections.length > 0) && (
                        <span className="flex items-center gap-1">
                            <span className="inline-block" style={{ width: 16, height: 2, background: MARKER_COLORS.arc, borderRadius: 1 }} />
                            Vaulted
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}
